import React from 'react'
import { Box, Container, Typography, Button, Stack, Chip } from '@mui/material'
import { Bolt, ArrowForward, CheckCircle } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

const plans = [
  {
    speed: '1.2Gb',
    mbps: 1200,
    price: '£46.50',
    label: 'Ucheldir',
    id: 'db0dde1d-6fc4-11f1-9c07-52540086d121',
    accent: '#00B140',
    accentBg: 'rgba(0, 177, 64, 0.12)',
    borderColor: 'rgba(0, 177, 64, 0.35)',
  },
  {
    speed: '1.8Gb',
    mbps: 1800,
    price: '£49.50',
    label: 'Penrhyn',
    id: 'db0e2ba1-6fc4-11f1-9c07-52540086d121',
    accent: '#DD2033',
    accentBg: 'rgba(221, 32, 51, 0.12)',
    borderColor: 'rgba(221, 32, 51, 0.35)',
  },
]

const features = [
  'Free installation',
  'No mid-contract price rises',
  'Wales-Based Technicians',
  'Static IP available',
]

const UltraFastSection: React.FC = () => {
  const navigate = useNavigate()

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 10 },
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(0,177,64,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Chip
            icon={<Bolt sx={{ fontSize: '14px !important', color: '#f57c00 !important' }} />}
            label="Just launched"
            sx={{
              mb: 2.5,
              bgcolor: 'rgba(245, 124, 0, 0.12)',
              border: '1px solid rgba(245, 124, 0, 0.3)',
              color: '#f57c00',
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.05em',
            }}
          />
          <Typography
            component="h2"
            sx={{
              color: 'white',
              fontWeight: 800,
              fontSize: { xs: 32, md: 48 },
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            Ultra-fast broadband,{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(90deg, #00B140 0%, #DD2033 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              now in Wales
            </Box>
          </Typography>
          <Typography
            sx={{
              color: '#A0AEC0',
              fontSize: { xs: 16, md: 18 },
              maxWidth: 540,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Our fastest ever plans \u2014 1.2Gb and 1.8Gb full-fibre \u2014 built for homes and businesses that demand the very best.
          </Typography>
        </Box>

        {/* Plan cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 3, md: 4 },
            maxWidth: 820,
            mx: 'auto',
            mb: { xs: 5, md: 6 },
          }}
        >
          {plans.map((plan) => (
            <Box
              key={plan.speed}
              sx={{
                bgcolor: '#121417',
                border: `1px solid ${plan.borderColor}`,
                borderRadius: 5,
                p: { xs: 3, md: 4 },
                position: 'relative',
                overflow: 'hidden',
                boxShadow: `0 0 40px ${plan.accentBg}`,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 16px 48px ${plan.accentBg}, 0 0 0 1px ${plan.borderColor}`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  background: `linear-gradient(90deg, transparent, ${plan.accent}, transparent)`,
                },
              }}
            >
              <Stack spacing={3}>
                {/* Speed & price */}
                <Box>
                  <Typography
                    sx={{
                      color: '#8A8F98',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      letterSpacing: '0.15em',
                      textTransform: 'uppercase',
                      mb: 0.5,
                    }}
                  >
                    {plan.label}
                  </Typography>
                  <Stack direction="row" alignItems="flex-end" spacing={1.5}>
                    <Typography
                      sx={{
                        color: 'white',
                        fontWeight: 900,
                        fontSize: { xs: 52, md: 64 },
                        lineHeight: 1,
                        letterSpacing: '-0.03em',
                        fontFeatureSettings: '"tnum" 1',
                      }}
                    >
                      {plan.speed}
                    </Typography>
                    <Box sx={{ pb: 1 }}>
                      <Bolt sx={{ color: plan.accent, fontSize: 28 }} />
                    </Box>
                  </Stack>
                  <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mt: 1 }}>
                    <Typography sx={{ color: 'white', fontWeight: 800, fontSize: 28, lineHeight: 1 }}>
                      {plan.price}
                    </Typography>
                    <Typography sx={{ color: '#8A8F98', fontSize: 14 }}>/month</Typography>
                  </Stack>
                </Box>

                {/* Divider */}
                <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.07)' }} />

                {/* Features */}
                <Stack spacing={1.25}>
                  {features.map((f) => (
                    <Stack key={f} direction="row" spacing={1.25} alignItems="center">
                      <CheckCircle sx={{ color: plan.accent, fontSize: 17, flexShrink: 0 }} />
                      <Typography sx={{ color: '#C9CDD2', fontSize: '0.875rem' }}>{f}</Typography>
                    </Stack>
                  ))}
                </Stack>

                {/* CTA */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate(`/packages/${plan.id}`)}
                  sx={{
                    borderColor: plan.accent,
                    color: plan.accent,
                    fontWeight: 700,
                    borderRadius: 3,
                    py: 1.25,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    '&:hover': {
                      bgcolor: plan.accentBg,
                      borderColor: plan.accent,
                    },
                  }}
                >
                  View {plan.speed} plan
                </Button>
              </Stack>
            </Box>
          ))}
        </Box>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            onClick={() => navigate('/get-started')}
            sx={{
              bgcolor: '#00B140',
              color: 'white',
              fontWeight: 700,
              fontSize: '1rem',
              px: 4,
              py: 1.5,
              borderRadius: 999,
              textTransform: 'none',
              boxShadow: '0 6px 24px rgba(0,177,64,0.35)',
              '&:hover': {
                bgcolor: '#008F33',
                boxShadow: '0 8px 32px rgba(0,177,64,0.45)',
              },
            }}
          >
            Check availability in your area
          </Button>
          <Typography sx={{ color: '#8A8F98', fontSize: '0.8rem', mt: 1.5 }}>
            12-month contract · Free installation · Upload speeds subject to confirmation
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

export default UltraFastSection
